import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircleIcon,
  ClockIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { apiClient } from '../../lib/api'
import LoadingSpinner from '../ui/LoadingSpinner'

interface AutomationStatsWidgetProps {
  projectId?: number
}

const AutomationStatsWidget: React.FC<AutomationStatsWidgetProps> = ({ projectId }) => {
  // Fetch automation stats
  const { data: statsData, isLoading } = useQuery({
    queryKey: ['automationStats', projectId],
    queryFn: () => apiClient.getAutomationStats(projectId),
    staleTime: 30000,
    refetchInterval: 60000, // Refresh every minute
  })

  if (isLoading) {
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg p-6">
        <div className="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  const stats = statsData?.data || {}

  const automationMetrics = [
    {
      name: 'AI Events (24h)',
      value: stats.events_24h || 0,
      icon: BoltIcon,
      color: 'blue',
      description: 'Comments auto-generated',
    },
    {
      name: 'Pending Approval',
      value: stats.pending_approvals || 0,
      icon: ClockIcon,
      color: 'yellow',
      description: 'Awaiting your review',
    },
    {
      name: 'Auto-Posted (24h)',
      value: stats.auto_posted_24h || 0,
      icon: CheckCircleIcon,
      color: 'green',
      description: 'Successfully posted',
    },
    {
      name: 'Error Rate',
      value: `${(stats.error_rate || 0).toFixed(1)}%`,
      icon: ExclamationTriangleIcon,
      color: stats.error_rate > 10 ? 'red' : 'gray',
      description: 'Failed automations',
    },
  ]

  const colorClasses = {
    blue: 'bg-blue-500',
    yellow: 'bg-yellow-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    gray: 'bg-gray-500',
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="bg-white overflow-hidden shadow rounded-lg"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-6 w-6 text-gray-900 mr-2" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Automation Activity
            </h3>
          </div>
          <Link
            to="/approvals"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Review pending
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {automationMetrics.map((metric, index) => (
            <motion.div
              key={metric.name}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="relative overflow-hidden rounded-lg bg-gray-50 px-4 py-4 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-start">
                <div className={`${colorClasses[metric.color as keyof typeof colorClasses]} rounded-md p-2`}>
                  <metric.icon className="h-5 w-5 text-white" aria-hidden="true" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-xs font-medium text-gray-500">{metric.name}</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">{metric.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Per-Project Breakdown (if stats available) */}
        {!projectId && stats.by_project && stats.by_project.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3">By Project</h4>
            <div className="space-y-2">
              {stats.by_project.slice(0, 5).map((project: any) => (
                <div
                  key={project.project_id}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-gray-700 truncate flex-1">
                    {project.project_name}
                  </span>
                  <div className="flex items-center space-x-4 ml-4">
                    <span className="text-gray-500">
                      {project.generated || 0} generated
                    </span>
                    <span className="text-green-600 font-medium">
                      {project.posted || 0} posted
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <Link
              to="/projects"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              Manage automation →
            </Link>
            {stats.pending_approvals > 0 && (
              <Link
                to="/approvals"
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Review {stats.pending_approvals} pending
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default AutomationStatsWidget
